/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useRef, useState } from 'react';
import {
  animate, motion, useMotionValue, useMotionValueEvent, useTransform,
} from 'framer-motion';

// React Bits "Elastic Slider" (https://reactbits.dev/components/elastic-slider) —
// a slider whose track/handle stretch elastically when dragged past the ends.
// Ported to framer-motion, light/black themed. onChange(value) fires while dragging.

const MAX_OVERFLOW = 50;

const decay = (value, max) => {
  if (max === 0) return 0;
  const entry = value / max;
  const sigmoid = 2 * (1 / (1 + Math.exp(-entry)) - 0.5);
  return sigmoid * max;
};

function Slider({
  defaultValue, startingValue, maxValue, isStepped, stepSize, leftIcon, rightIcon, onChange,
}) {
  const [value, setValue] = useState(defaultValue);
  const sliderRef = useRef(null);
  const [region, setRegion] = useState('middle');
  const clientX = useMotionValue(0);
  const overflow = useMotionValue(0);
  const scale = useMotionValue(1);

  useEffect(() => { setValue(defaultValue); }, [defaultValue]);

  useMotionValueEvent(clientX, 'change', (latest) => {
    if (sliderRef.current) {
      const { left, right } = sliderRef.current.getBoundingClientRect();
      let newValue;
      if (latest < left) { setRegion('left'); newValue = left - latest; } else if (latest > right) { setRegion('right'); newValue = latest - right; } else { setRegion('middle'); newValue = 0; }
      overflow.jump(decay(newValue, MAX_OVERFLOW));
    }
  });

  const commit = (v) => { setValue(v); onChange?.(v); };

  const handlePointerMove = (e) => {
    if (e.buttons > 0 && sliderRef.current) {
      const { left, width } = sliderRef.current.getBoundingClientRect();
      let newValue = startingValue + ((e.clientX - left) / width) * (maxValue - startingValue);
      if (isStepped) newValue = Math.round(newValue / stepSize) * stepSize;
      newValue = Math.min(Math.max(newValue, startingValue), maxValue);
      commit(newValue);
      clientX.jump(e.clientX);
    }
  };

  const handlePointerDown = (e) => {
    handlePointerMove(e);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = () => { animate(overflow, 0, { type: 'spring', bounce: 0.5 }); };

  const rangePct = () => {
    const total = maxValue - startingValue;
    return total === 0 ? 0 : ((value - startingValue) / total) * 100;
  };

  return (
    <>
      <motion.div
        onHoverStart={() => animate(scale, 1.05)}
        onHoverEnd={() => animate(scale, 1)}
        onTouchStart={() => animate(scale, 1.05)}
        onTouchEnd={() => animate(scale, 1)}
        // Gentle hover lift (1.05, was 1.2) — a bigger zoom pushed the end
        // labels past the container edge, where the card's overflow clip ate
        // the last character (e.g. "30s" -> "30").
        style={{ scale, opacity: useTransform(scale, [1, 1.05], [0.75, 1]) }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <motion.div
            animate={{ scale: region === 'left' ? [1, 1.35, 1] : 1 }}
            transition={{ duration: 0.25 }}
            style={{
              x: useTransform(() => (region === 'left' ? -overflow.get() / scale.get() : 0)),
              color: '#98a2b3',
              display: 'inline-flex',
            }}
          >
            {leftIcon}
          </motion.div>

          <div
            ref={sliderRef}
            onPointerMove={handlePointerMove}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            style={{
              position: 'relative',
              display: 'flex',
              flexGrow: 1,
              cursor: 'grab',
              touchAction: 'none',
              alignItems: 'center',
              padding: '12px 0',
            }}
          >
            <motion.div
              style={{
                scaleX: useTransform(() => {
                  if (sliderRef.current) {
                    const { width } = sliderRef.current.getBoundingClientRect();
                    return 1 + overflow.get() / width;
                  }
                  return 1;
                }),
                scaleY: useTransform(overflow, [0, MAX_OVERFLOW], [1, 0.8]),
                transformOrigin: useTransform(() => {
                  if (sliderRef.current) {
                    const { left, width } = sliderRef.current.getBoundingClientRect();
                    return clientX.get() < left + width / 2 ? 'right' : 'left';
                  }
                  return 'center';
                }),
                height: useTransform(scale, [1, 1.05], [6, 10]),
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div style={{
                position: 'relative', width: '100%', height: '100%', borderRadius: 999, background: '#e4e7ec', overflow: 'hidden',
              }}
              >
                <div style={{ height: '100%', background: '#15171c', borderRadius: 999, width: `${rangePct()}%` }} />
              </div>
            </motion.div>
          </div>

          <motion.div
            animate={{ scale: region === 'right' ? [1, 1.35, 1] : 1 }}
            transition={{ duration: 0.25 }}
            style={{
              x: useTransform(() => (region === 'right' ? overflow.get() / scale.get() : 0)),
              color: '#98a2b3',
              display: 'inline-flex',
            }}
          >
            {rightIcon}
          </motion.div>
        </div>
      </motion.div>
      <p style={{
        margin: '6px 0 0', textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#15171c', fontVariantNumeric: 'tabular-nums',
      }}
      >
        {Math.round(value)}
      </p>
    </>
  );
}

export default function ElasticSlider({
  defaultValue = 50,
  startingValue = 0,
  maxValue = 100,
  isStepped = false,
  stepSize = 1,
  leftIcon = '–',
  rightIcon = '+',
  onChange,
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', width: '100%', padding: '0 6px', boxSizing: 'border-box',
    }}
    >
      <Slider
        defaultValue={defaultValue}
        startingValue={startingValue}
        maxValue={maxValue}
        isStepped={isStepped}
        stepSize={stepSize}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        onChange={onChange}
      />
    </div>
  );
}
